import {
  Landmark,
  BookOpen,
  Briefcase,
  Building2,
  Users,
  ShieldCheck,
  GraduationCap,
  Cpu,
  LineChart,
  Wrench,
  Mic,
  Award,
  ArrowRight,
} from "lucide-react";
import PageHero from "../components/site/PageHero";
import { Reveal } from "../components/site/Reveal";
import { Overline, PrimaryButton, GoldButton } from "../components/site/Primitives";
import { openPartners } from "../lib/site";

const CORE = [
  { icon: Briefcase, title: "Workforce Development" },
  { icon: GraduationCap, title: "Vocational Education" },
  { icon: Cpu, title: "AI Training" },
  { icon: LineChart, title: "Small Business Development" },
  { icon: BookOpen, title: "Curriculum Design" },
  { icon: Users, title: "Instructor Services" },
  { icon: Wrench, title: "Technical Assistance" },
  { icon: Mic, title: "Program Facilitation" },
];

const PARTNERS = [
  { icon: Landmark, label: "Government agencies" },
  { icon: BookOpen, label: "Educational institutions" },
  { icon: Users, label: "Workforce development organizations" },
  { icon: Building2, label: "Nonprofit organizations" },
  { icon: Briefcase, label: "Small business programs" },
  { icon: Wrench, label: "Industry associations" },
];

const SERVICES = [
  "Workforce training",
  "AI workshops",
  "Business consulting",
  "Vocational education",
  "Curriculum development",
  "Professional development",
];

const CREDENTIALS = [
  "20+ Years of Industry Experience",
  "NYS Approved Instructor",
  "Master of Public Administration",
  "Certified Workforce Trainer",
  "AI Business Consultant",
  "Curriculum Developer",
  "Government Contract Ready",
];

export default function Partners() {
  return (
    <div data-testid="partners-page">
      <PageHero
        overline="Partnerships"
        title="Beauty was the beginning. Partnerships are what's next."
        subtitle="I'm building Nervani into a home for education, workforce training, and business consulting — grounded in beauty, expanding into every industry that trains its people. If you lead an organization ready to invest in growth, I'd love to build with you."
      >
        <PrimaryButton onClick={openPartners} data-testid="partners-hero-btn">Partner With Us <ArrowRight size={16} /></PrimaryButton>
        <GoldButton as="link" to="/industries" data-testid="partners-industries-btn">Industries Served</GoldButton>
      </PageHero>

      {/* Core Competencies */}
      <section data-testid="core-competencies" className="bg-brand-cream py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl mb-14">
            <Overline className="mb-5">Areas of Focus</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl lg:text-6xl tracking-tight">What I&apos;ve built to help you grow.</h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CORE.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05}>
                <div className="h-full rounded-3xl bg-white border border-brand-ink/5 p-7">
                  <div className="h-12 w-12 rounded-2xl bg-brand-blush flex items-center justify-center text-brand-rose">
                    <c.icon size={22} strokeWidth={1.6} />
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-brand-ink tracking-tight mt-5 leading-tight">{c.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* We partner with + Services */}
      <section className="bg-brand-blush py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
          <Reveal>
            <div className="rounded-3xl bg-white border border-brand-ink/5 p-9 md:p-11 h-full">
              <Overline className="mb-5">We partner with</Overline>
              <ul className="grid gap-4" data-testid="partner-with-list">
                {PARTNERS.map((p) => (
                  <li key={p.label} className="flex items-center gap-4">
                    <div className="h-11 w-11 rounded-2xl bg-brand-blush flex items-center justify-center text-brand-rose flex-shrink-0">
                      <p.icon size={18} strokeWidth={1.7} />
                    </div>
                    <span className="text-brand-ink text-base font-light">{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-brand-ink border border-brand-ink text-brand-cream p-9 md:p-11 h-full">
              <Overline dark className="mb-5">Services include</Overline>
              <ul className="grid gap-3" data-testid="services-include-list">
                {SERVICES.map((s) => (
                  <li key={s} className="flex items-center gap-3 border-b border-white/10 py-3 last:border-0">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-gold flex-shrink-0" />
                    <span className="text-brand-cream text-base font-light">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Credentials strip */}
      <section data-testid="partners-credentials" className="bg-brand-cream py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal className="max-w-2xl mb-12">
            <Overline className="mb-5">Credentials & Qualifications</Overline>
            <h2 className="font-serif font-light text-brand-ink text-4xl sm:text-5xl tracking-tight">Vetted for the room.</h2>
          </Reveal>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CREDENTIALS.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <div className="flex items-center gap-3 rounded-2xl border border-brand-ink/10 bg-white px-5 py-4">
                  <ShieldCheck size={18} className="text-brand-gold flex-shrink-0" />
                  <span className="text-brand-ink text-[15px] font-light">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-ink py-24 md:py-28 text-brand-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center flex flex-col items-center">
          <Award size={28} className="text-brand-gold mb-5" />
          <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Let&apos;s build something meaningful.</h2>
          <p className="text-brand-cream/70 font-light mt-5 max-w-lg">
            Send your RFP, scope, or a short description of your workforce goal.
            We&apos;ll respond with a fit assessment and proposed approach.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={openPartners}
              data-testid="partners-cta-btn"
              className="btn-fill bg-brand-gold text-brand-ink px-8 py-3.5 rounded-full text-sm tracking-wide font-medium"
            >
              Start a Partnership
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
